import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerSyn() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" }
  ];
  const tocItems = [
    { id: "skal-trailer-synes", title: "Skal en trailer til syn? (Periodisk syn vs. ejerskifte)" },
    { id: "ejerskifte-regler", title: "Syn ved ejerskifte og omregistrering" },
    { id: "tempo-100-syn", title: "Synsregler ved Tempo 100-godkendelse" },
    { id: "hvad-tjekkes", title: "Hvad kigger synsmanden på ved et trailersyn?" },
    { id: "syn-pris", title: "Hvad koster et syn af trailer?" }
  ];

  const faq = [
    {
      q: "Bliver man automatisk indkaldt til periodisk syn med en almindelig trailer?",
      a: "Nej. Almindelige påhængsvogne til personbiler (under 3.500 kg) indkaldes ikke automatisk til periodisk syn i Danmark, medmindre traileren er godkendt til Tempo 100 og har en tilladt totalvægt over 750 kg."
    },
    {
      q: "Skal en trailer på 500 kg synes ved ejerskifte?",
      a: "Nej. Trailere med en tilladt totalvægt på 500 kg eller derunder er helt fritaget for syn ved ejerskifte, uanset hvor gamle de er. De kan omregistreres direkte i Motorregistret."
    },
    {
      q: "Hvornår skal en trailer over 500 kg synes ved salg?",
      a: "En trailer over 500 kg skal synes ved ejerskifte, hvis den er mellem 5 og 10 år gammel og ikke er godkendt ved syn inden for de seneste 2 år. Er traileren over 10 år gammel, skal den være synet inden for det seneste 1 år for at kunne omregistreres uden nyt syn."
    },
    {
      q: "Hvad koster det at få en trailer synet?",
      a: "Prisen for et almindeligt trailersyn ligger typisk i spændet 350 til 550 kr. hos private godkendte synshaller. Prisen varierer efter synshal og om der er tale om en bremset eller ubremset trailer."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om godkendelse og syn af køretøjer (BEK nr. 1429 af 29/11/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1429" },
    { title: "Færdselsstyrelsen: Periodisk syn og kontrol af køretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/syn/periodisk-syn" },
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" }
  ];

  return (
    <>
      <SEO 
        title="Trailer syn: Regler, frister, ejerskifte og priser" 
        description="Få overblik over syn af trailer i Danmark: hvornår skal traileren synes ved ejerskifte, frister efter alder og vægt samt hvad synsmanden kontrollerer." 
        pathname="/trailer-syn"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Syn af trailer: Regler, frister og ejerskifte
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          En almindelig trailer til personbil <strong>indkaldes ikke automatisk til periodisk syn</strong> i Danmark. Syn er kun påkrævet i to situationer: ved <strong>ejerskifte (omregistrering)</strong> af ældre trailere over 500 kg, eller hvis traileren er godkendt til <strong>Tempo 100</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Trailere med en tilladt totalvægt på 500 kg eller derunder skal aldrig synes – hverken løbende eller ved salg. For trailere over 500 kg afhænger synskravet ved salg af trailerens alder og hvornår den sidst har været godkendt i en synshal.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Kort overblik over synsregler">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Trailer &le; 500 kg: <strong>Aldrig syn</strong> (hverken løbende eller ved salg).</li>
            <li>• Trailer 501–3.500 kg under 5 år: <strong>Intet syn ved ejerskifte</strong>.</li>
            <li>• Trailer 501–3.500 kg (5–10 år): Syn kræves ved salg, hvis &gt; 2 år siden sidste syn.</li>
            <li>• Trailer 501–3.500 kg (&gt; 10 år): Syn kræves ved salg, hvis &gt; 1 år siden sidste syn.</li>
            <li>• Tempo 100-trailer (&gt; 750 kg): Periodisk syn hvert <strong>2. år</strong>.</li>
          </ul>
        </Callout>

        <h2 id="skal-trailer-synes" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Skal en trailer til syn? (Periodisk syn vs. ejerskifte)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange trailer-ejere forveksler reglerne for personbiler med reglerne for påhængsvogne. Hvor personbiler automatisk modtager en digital indkaldelse fra Færdselsstyrelsen hvert andet år efter bilens 4-års fødselsdag, er der som udgangspunkt <strong>ingen periodiske synsindkaldelser</strong> for private trailere under 3.500 kg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Du modtager altså intet brev i e-Boks, blot fordi du har haft traileren stående i haven i 15 år. Men hvis traileren skal sælges og omregistreres til en ny ejer, træder de lovfastsatte ejerskiftefrister i kraft.
        </p>

        <h2 id="ejerskifte-regler" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Syn ved ejerskifte og omregistrering
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til synsbekendtgørelsen gælder følgende regler, når en trailer skifter ejer og skal omregistreres hos Motorstyrelsen:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Trailerens totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Trailerens alder</th>
                <th className="py-2.5 pr-4 font-semibold">Krav ved ejerskifte</th>
                <th className="py-2.5 font-semibold">Synsgyldighed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Højst 500 kg</td>
                <td className="py-3 pr-4">Uanset alder</td>
                <td className="py-3 pr-4 font-semibold text-[#B58200]">Fritaget for syn</td>
                <td className="py-3 text-[#5C636B]">Omregistreres direkte uden syn</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">501 – 3.500 kg</td>
                <td className="py-3 pr-4">Under 5 år</td>
                <td className="py-3 pr-4">Skal ikke synes</td>
                <td className="py-3 text-[#5C636B]">Omregistreres på eksisterende registrering</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">501 – 3.500 kg</td>
                <td className="py-3 pr-4">5 – 10 år</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">Skal synes ved salg</td>
                <td className="py-3 text-[#5C636B]">Medmindre godkendt inden for de seneste 2 år</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">501 – 3.500 kg</td>
                <td className="py-3 pr-4">Over 10 år</td>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Skal synes ved salg</td>
                <td className="py-3 text-[#5C636B]">Medmindre godkendt inden for det seneste 1 år</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Har traileren over 500 kg ikke et gyldigt syn inden for den anførte frist, vil Motorregistret automatisk afvise omregistreringen, indtil en synsrapport med resultatet "Godkendt" er indberettet af en synshal.
        </p>

        <h2 id="tempo-100-syn" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Synsregler ved Tempo 100-godkendelse
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis du vælger at få din trailer godkendt til <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">dansk Tempo 100</Link>, ændrer synsforpligtelsen sig:
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Har traileren en tilladt totalvægt på <strong>mere end 750 kg</strong>, overgår den automatisk til Færdselsstyrelsens system for periodiske syn. Den vil derefter blive indkaldt til syn hvert <strong>2. år</strong> for at opretholde sin 100 km/t-godkendelse.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Hvis Tempo 100-godkendelsen afmeldes hos Motorstyrelsen, bortfalder det periodiske synsinterval igen.
        </p>

        <h2 id="hvad-tjekkes" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad kigger synsmanden på ved et trailersyn?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Ved et almindeligt registreringssyn af en trailer gennemgår synsassistenten køretøjets bærende og trafiksikkerhedsmæssige komponenter:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Stelnr. og typeskilt:</strong> Stelnummeret skal være tydeligt indpræget i chassisrammen og stemme overens med registreringsattesten.</li>
          <li><strong>Koblingshoved og trækstang:</strong> Slør i kuglekoblingen, slidindikator, fungerende låsemekanisme og intakt sikkerhedswire (springwire).</li>
          <li><strong>Bremser (hvis monteret):</strong> Funktionsprøve på bremsefelt. Ensartet bremsevirkning i begge sider, påløbsdæmperens modstand samt fungerende håndbremse.</li>
          <li><strong>Hjul, aksler og lejer:</strong> Slør eller mislyde i hjullejer, gennemtæring af akselrør samt dækkenes tilstand (mindst 1,6 mm mønster, ingen revner eller krakeleringer).</li>
          <li><strong>Lygter og reflekser:</strong> Lovpligtigt lysanlæg (baglys, bremselys, blinklys, nummerpladelygte og de to trekantede røde bagreflekser). Se <Link to="/trailer-lys-regler" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">regler for trailerlygter</Link>.</li>
        </ul>

        <h2 id="syn-pris" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad koster et syn af trailer?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Da synshaller i Danmark er private virksomheder med fri prissætning, varierer prisen:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Ubremset trailer:</strong> Typisk 350 – 450 kr.</li>
          <li><strong>Bremset trailer:</strong> Typisk 450 – 550 kr. (da bremserne skal testes i rullefelt).</li>
          <li><strong>Tempo 100-førstegangssyn:</strong> Typisk 550 – 850 kr. inkl. opmåling og dataindberetning.</li>
          <li><strong>Omsyn (ved konstaterede fejl):</strong> Typisk 250 – 350 kr. inden for 60 dage.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også om omregistrering og pladekøb i guiden om <Link to="/trailer-nummerplade" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer nummerplader</Link> og vægtklasser i <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
